import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock3, Lightbulb } from "lucide-react";
import { Reveal } from "../components/motion";
import CTABand from "../components/CTABand";
import { api } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: article ? `${article.title} — The Nanny Sleep Co.` : "Sleep Help — The Nanny Sleep Co.",
    description: article ? article.excerpt : "Gentle, practical sleep guidance.",
    schema: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          author: { "@type": "Person", name: "Tamika", jobTitle: "Certified infant & child sleep consultant" },
          publisher: { "@type": "Organization", name: "The Nanny Sleep Co." },
        }
      : undefined,
  });
  usePageView(`/sleep-help/${slug}`);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.article(slug).then(setArticle).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="article-not-found">
        <h1 className="font-serif text-3xl text-ink mb-4">That page has drifted off to sleep</h1>
        <Link to="/sleep-help" className="btn-primary">Back to Sleep Help</Link>
      </div>
    );
  }

  if (!article) return <div className="min-h-[60vh]" />;

  return (
    <div data-testid="article-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <Reveal>
          <Link to="/sleep-help" data-testid="article-back" className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> All sleep help
          </Link>
          <div className="flex items-center gap-2.5 mb-5 flex-wrap">
            <span className="text-xs font-mono uppercase tracking-widest text-terra font-semibold">{article.age_tag}</span>
            <span className="w-1 h-1 rounded-full bg-ink/20" />
            <span className="text-xs text-ink-muted">{article.problem_tag}</span>
            <span className="w-1 h-1 rounded-full bg-ink/20" />
            <span className="text-xs text-ink-muted flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" />{article.read_time}</span>
          </div>
          <h1 className="font-serif font-light text-3xl sm:text-4xl lg:text-[2.9rem] text-ink leading-[1.15] tracking-tight mb-8">{article.title}</h1>
          <div className="flex items-center gap-3.5 pb-10 mb-10 border-b border-ink/[0.07]">
            <img
              src="https://images.squarespace-cdn.com/content/v1/68b7e04ed5afe32710b42d83/8cef08b2-5d57-44a4-bb44-244be6da555c/IMG_5703.jpg"
              alt="Tamika"
              className="w-11 h-11 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-semibold text-ink">Tamika</p>
              <p className="text-xs text-ink-muted">Certified infant & child sleep consultant</p>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="card-soft !bg-sage-light/50 border-sage/20 p-6 sm:p-7 mb-12 flex gap-4">
            <Lightbulb className="w-6 h-6 text-sage shrink-0 mt-0.5" strokeWidth={1.8} />
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-sage font-semibold mb-1.5">The short version</p>
              <p className="text-ink leading-relaxed">{article.takeaway}</p>
            </div>
          </div>
        </Reveal>

        <div className="space-y-12">
          {article.sections.map((s, i) => (
            <Reveal key={i}>
              <section>
                <h2 className="font-serif text-2xl sm:text-[1.7rem] font-medium text-ink mb-4 leading-snug">{s.heading}</h2>
                <p className="text-ink-soft leading-[1.85] text-[1.05rem]">{s.body}</p>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14">
          <div className="border-l-2 border-terra/40 pl-5 py-1 text-sm text-ink-muted leading-relaxed">
            This article is general sleep education, not individual or medical advice. Every child is different — if you have health concerns, please speak with your GP or child health nurse.
          </div>
        </Reveal>

        <Reveal className="mt-12">
          <div className="card-soft p-8 sm:p-10 text-center !bg-terra-light/50 border-terra/20">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-ink mb-3">Want this tailored to your child?</h2>
            <p className="text-ink-soft mb-7 max-w-md mx-auto">Two minutes of gentle questions, one clear next step for your family.</p>
            <Link to="/sleep-assessment" data-testid="article-assessment-cta" className="btn-primary">Get your tailored next step</Link>
          </div>
        </Reveal>
      </article>
      <CTABand />
    </div>
  );
}
